import { useEffect, useId, useRef, useState } from 'react';

import { formatMeetingDate, todayMeetingDate } from '../meetingLabels';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;
const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const;

function parseYmd(value: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  return { year, month, day };
}

function toYmd(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** Понедельник = 0 … воскресенье = 6. */
function mondayBasedWeekday(year: number, month: number, day: number): number {
  const js = new Date(year, month - 1, day).getDay();
  return js === 0 ? 6 : js - 1;
}

type MeetingDatePickerProps = {
  id: string;
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange: (next: string) => void;
};

export function MeetingDatePicker({
  id,
  value,
  disabled = false,
  invalid = false,
  onChange,
}: MeetingDatePickerProps) {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const parsed = parseYmd(value);
  const today = todayMeetingDate();
  const todayParsed = parseYmd(today) ?? { year: 2026, month: 1, day: 1 };

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsed?.year ?? todayParsed.year);
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? todayParsed.month);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPicker = () => {
    if (disabled) {
      return;
    }
    const base = parsed ?? todayParsed;
    setViewYear(base.year);
    setViewMonth(base.month);
    setOpen(true);
  };

  const shiftMonth = (delta: number) => {
    const date = new Date(viewYear, viewMonth - 1 + delta, 1);
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth() + 1);
  };

  const firstWeekday = mondayBasedWeekday(viewYear, viewMonth, 1);
  const totalDays = daysInMonth(viewYear, viewMonth);
  const cells: Array<{ ymd: string; day: number; inMonth: boolean }> = [];

  const prevMonth = viewMonth === 1 ? 12 : viewMonth - 1;
  const prevYear = viewMonth === 1 ? viewYear - 1 : viewYear;
  const prevDays = daysInMonth(prevYear, prevMonth);
  for (let i = firstWeekday - 1; i >= 0; i -= 1) {
    const day = prevDays - i;
    cells.push({ ymd: toYmd(prevYear, prevMonth, day), day, inMonth: false });
  }
  for (let day = 1; day <= totalDays; day += 1) {
    cells.push({ ymd: toYmd(viewYear, viewMonth, day), day, inMonth: true });
  }
  const nextMonth = viewMonth === 12 ? 1 : viewMonth + 1;
  const nextYear = viewMonth === 12 ? viewYear + 1 : viewYear;
  let nextDay = 1;
  while (cells.length % 7 !== 0 || cells.length < 42) {
    cells.push({ ymd: toYmd(nextYear, nextMonth, nextDay), day: nextDay, inMonth: false });
    nextDay += 1;
    if (cells.length >= 42) {
      break;
    }
  }

  const display = value.trim() !== '' ? formatMeetingDate(value) : 'Выберите дату';

  return (
    <div className="date-picker" ref={rootRef}>
      <button
        id={id}
        type="button"
        className="field__control date-picker__trigger"
        disabled={disabled}
        aria-invalid={invalid}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => (open ? setOpen(false) : openPicker())}
      >
        <span className={value.trim() === '' ? 'date-picker__placeholder' : undefined}>
          {display}
        </span>
      </button>

      {open ? (
        <div
          id={listboxId}
          className="date-picker__popover"
          role="dialog"
          aria-label="Календарь даты встречи"
        >
          <div className="date-picker__header">
            <button
              type="button"
              className="date-picker__nav"
              aria-label="Предыдущий месяц"
              onClick={() => shiftMonth(-1)}
            >
              ‹
            </button>
            <span className="date-picker__title">
              {MONTHS[viewMonth - 1]} {viewYear}
            </span>
            <button
              type="button"
              className="date-picker__nav"
              aria-label="Следующий месяц"
              onClick={() => shiftMonth(1)}
            >
              ›
            </button>
          </div>

          <div className="date-picker__weekdays" aria-hidden="true">
            {WEEKDAYS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          <div className="date-picker__grid">
            {cells.map((cell) => {
              const selected = cell.ymd === value;
              const isToday = cell.ymd === today;
              return (
                <button
                  key={cell.ymd}
                  type="button"
                  className={[
                    'date-picker__day',
                    cell.inMonth ? null : 'date-picker__day--outside',
                    selected ? 'date-picker__day--selected' : null,
                    isToday ? 'date-picker__day--today' : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    onChange(cell.ymd);
                    setOpen(false);
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          <div className="date-picker__footer">
            <button
              type="button"
              className="date-picker__action"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Очистить
            </button>
            <button
              type="button"
              className="date-picker__action"
              onClick={() => {
                onChange(today);
                setOpen(false);
              }}
            >
              Сегодня
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
